import React from "react";
import { MAX_MISTAKES } from "../../lib/constants";
import { PuzzleDataContext } from "../PuzzleData";
import {
    loadGameStateFromLocalStorage,
    saveGameStateToLocalStorage,
} from "../../lib/local-storage";
import {
    isGameDataEquivalent,
    isGuessesFromGame,
} from "../../lib/game-helpers";
export const GameStatusContext = React.createContext();

function GameStatusProvider({ children }) {
    const { gameData, isLoading } = React.useContext(PuzzleDataContext);
    const [submittedGuesses, setSubmittedGuesses] = React.useState([]);
    const [solvedGameData, setSolvedGameData] = React.useState([]);
    const hasRestoredState = React.useRef(false);
    // Blocks saves until after the restore attempt completes, preventing the
    // initial empty state from overwriting saved progress in localStorage.
    const [hasInitialized, setHasInitialized] = React.useState(false);

    // Restore saved game state only after the real puzzle has finished loading.
    // The save effects below are gated on hasInitialized so they cannot clobber
    // localStorage before this restore runs.
    React.useEffect(() => {
        if (isLoading || hasRestoredState.current) return;
        hasRestoredState.current = true;

        const loadedState = loadGameStateFromLocalStorage();
        if (
            loadedState &&
            Array.isArray(loadedState.submittedGuesses) &&
            isGameDataEquivalent({ gd1: gameData, gd2: loadedState.gameData }) &&
            isGuessesFromGame({ gameData, submittedGuesses: loadedState.submittedGuesses })
        ) {
            setSubmittedGuesses(loadedState.submittedGuesses);
            if (Array.isArray(loadedState.solvedGameData)) {
                setSolvedGameData(loadedState.solvedGameData);
            }
        }

        setHasInitialized(true);
    }, [isLoading, gameData]);

    const [isGameOver, setIsGameOver] = React.useState(false);
    const [isGameWon, setIsGameWon] = React.useState(false);
    const [guessCandidate, setGuessCandidate] = React.useState([]);

    const numMistakesUsed = submittedGuesses.length - solvedGameData.length;

    // use effect to check if game is won
    React.useEffect(() => {
        if (!hasInitialized) return;
        if (solvedGameData.length === gameData.length) {
            setIsGameOver(true);
            setIsGameWon(true);
        }
        saveGameStateToLocalStorage({ submittedGuesses, solvedGameData, gameData });
    }, [hasInitialized, solvedGameData, gameData, submittedGuesses]);

    // use effect to check if all mistakes have been used and end the game accordingly
    React.useEffect(() => {
        if (!hasInitialized) return;
        if (numMistakesUsed >= MAX_MISTAKES) {
            setIsGameOver(true);
            setIsGameWon(false);
        }
        saveGameStateToLocalStorage({ submittedGuesses, solvedGameData, gameData });
    }, [hasInitialized, submittedGuesses, numMistakesUsed, gameData, solvedGameData]);

    return (
        <GameStatusContext.Provider
            value={{
                isGameOver,
                isGameWon,
                numMistakesUsed,
                solvedGameData,
                setSolvedGameData,
                submittedGuesses,
                setSubmittedGuesses,
                guessCandidate,
                setGuessCandidate,
            }}
        >
            {children}
        </GameStatusContext.Provider>
    );
}

export default GameStatusProvider;
